export enum InsightsTab {
    Weekly = 'weekly',
    Yesterday = 'yesterday',
}

export enum StorePerformanceGrouping {
    Pyramid = 'pyramid',
    Department = 'department',
}

/**
 * Maps the backend-provided `insight_icon` key (SCREAMING_SNAKE_CASE, stable
 * across runs for a given insight type) to the SVG asset path under
 * `public/insights/`.
 *
 * Keep this in sync with the icon set in Figma. Unknown or missing keys fall
 * back to the generic "Unidentified" icon.
 */
const ICON_BY_KEY: Record<string, string> = {
    // Pyramid
    ESSENTIALS_AND_BEAUTY: '/insights/Essentials_Beauty.svg',
    FOOD_AND_BEVERAGE: '/insights/Food_Beverage.svg',
    FUN_101: '/insights/Fun.svg',
    HOME: '/insights/Home.svg',

    // Department
    HEALTH: '/insights/Health.svg',
    BEAUTY: '/insights/Beauty.svg',
    HOME_CARE: '/insights/Home_Care.svg',
    SNACKS_CANDY: '/insights/Snacks_Candy.svg',
    BABY: '/insights/Baby.svg',
    GADGET: '/insights/Gadget.svg',
    DRY_GROCERY: '/insights/Dry_Grocery.svg',
    PLAY: '/insights/Play.svg',
    NA_BEVERAGES: '/insights/NA_Beverages.svg',
    OUTDOOR_LIVING: '/insights/Outdoor_Living.svg',
    POP: '/insights/Pop.svg',
    CREATE_AND_CELEBRATE: '/insights/Create_Celebrate.svg',
    DOMESTICS: '/insights/Domestics.svg',
    KITCHEN: '/insights/Kitchen.svg',
    PRODUCE_AND_FLORAL: '/insights/Produce_Floral.svg',
    PETS: '/insights/Pets.svg',
    SPORT: '/insights/Sport.svg',
    MEN: '/insights/Men.svg',
    READY_TO_WEAR: '/insights/Ready_to_Wear.svg',
    KIDS_APPAREL: '/insights/Kids_Apparel.svg',
    NEWBORN_INFANT_APPAREL: '/insights/NIT_Apparel.svg',
    INT_AND_SLEEP: '/insights/INT_Sleep.svg',
    YOUNG_CONTEMPORARY: '/insights/Young_Contemporary.svg',
    DECORATIVE_HOME: '/insights/Decorative_Home.svg',
    PERFORMANCE: '/insights/Performance.svg',
    STARBUCKS: '/insights/Starbucks.svg',
    SHOES: '/insights/Shoes.svg',
    JEWELRY_ACCESSORIES: '/insights/Jewelry_Accessories.svg',
    PROCESSED_AND_FROZEN_MEATS: '/insights/Processed_Frozen_Meats.svg',
    SWIM: '/insights/Swim.svg',
    ADULT_BEVERAGES: '/insights/Adult_Beverages.svg',
    FRESH_MEAT: '/insights/Fresh_Meat.svg',
    DELI: '/insights/Deli.svg',
    ULTA: '/insights/Ulta.svg',
    BAKERY: '/insights/Bakery.svg',
    CAFE: '/insights/Cafe.svg',
    SPECIAL_PROJECT: '/insights/Special_Project.svg',
    LTO: '/insights/LTO.svg',

    // Dairy
    DAIRY_FROZEN: '/insights/Dairy.svg',

    // Channel / scope
    DISTRICT: '/insights/District.svg',
    ONLINE: '/insights/Online.svg',
    TARGET_OPTICAL: '/insights/Target_Optical.svg',

    UNIDENTIFIED: '/insights/Unidentified.svg',
}

const DEFAULT_ICON = '/insights/Unidentified.svg'

export function getInsightIcon(iconKey?: string): string {
    if (!iconKey) return DEFAULT_ICON
    return ICON_BY_KEY[iconKey] ?? DEFAULT_ICON
}

export function buildDetailsUrl(
    index: number,
    search: string,
    tab: InsightsTab,
): string {
    const params = new URLSearchParams(search)
    params.set('index', String(index))
    params.set('tab', tab)
    return `/insights/details?${params.toString()}`
}

export function buildSummaryDetailsUrl(
    search: string,
    tab: InsightsTab,
): string {
    const params = new URLSearchParams(search)
    params.set('source', 'summary')
    params.set('tab', tab)
    params.delete('index')
    return `/insights/details?${params.toString()}`
}

export const formatPercentChange = (value?: number): string | null => {
    if (value === undefined) return null
    const sign = value > 0 ? '+' : ''
    return `${sign}${value.toFixed(1)}%`
}

export const stringToTitleCase = (str?: string | null): string | null =>
    str?.length
        ? str.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase())
        : null

// \u200B is a zero-width space, which allows us to add a break point for long strings with slashes
// without affecting the display of the string
export const addHiddenSpaceAfterSlashForWrapping = (
    str?: string,
): string | null => (str ? str.replace(/\//g, '/\u200B') : null)

export const formatCompactCurrency = (value: number): string =>
    new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        ...(Math.abs(value) >= 1_000_000
            ? {
                  notation: 'compact',
                  maximumFractionDigits: 2,
                  minimumFractionDigits: 0,
              }
            : {
                  maximumFractionDigits: 2,
                  minimumFractionDigits: 0,
              }),
    }).format(value)
